import { AnimatePresence, MotionConfig, motion } from "motion/react";
import {
  fadeInAnimation,
  widgetContainerAnimation,
  frameloopFade,
} from "../../utils/animations";
import {
  getWidgetCloseEvent,
  getWidgetOpenEvent,
  getWidgetStateChangeEvent,
} from "@/constants/widget-events";
import { routerInGame, routerOnSite } from "../../router";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  CampaignNavigationContext,
  type NavigateToCampaign,
} from "@/contexts/campaign-navigation";
import { PortalContainerContext } from "@/contexts/portal-container";
import { RouterProvider } from "react-router-dom";
import { Toaster } from "@/components/notification/toaster";
import { InGameCampaignDock } from "@/components/in-game-campaign-dock";
import { TabRowCloseButton } from "@/components/ui/glass";
import { useHasMultipleSwitchableCampaigns } from "@/hooks/use-switchable-campaigns";
import { cn } from "@/lib/utils";
import { isCampaignRoute } from "@/utils/route-helpers";
import { campaignRoute, homeRoute } from "@/routes";
import { useApplyTheme } from "../../hooks/use-apply-theme";
import { useAppStore } from "@/stores";
import { useWidget } from "../../hooks/widget";
import { useWidgetVariant } from "@/contexts/widget-variant";
import {
  useEscapesGameFrame,
  useHoistHostToBody,
} from "@/hooks/use-escape-game-frame";
import { useInGameModal } from "@/hooks/use-in-game-modal";
import { useTranslation } from "react-i18next";
import {
  collectCampaignEntries,
  collectInWindowFinishedEntries,
  matchesKey,
} from "@/campaigns";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";
import { ClosedPanelReveal } from "@/components/prize-reveal/closed-panel-reveal";

export const WidgetContainer = () => {
  const variant = useWidgetVariant();
  const isInGame = variant === "in-game";
  // The phone campaign dock only exists when there is something to switch to; with a single
  // campaign the panel gets its normal inset back and nothing hangs off its bottom edge.
  const hasMultipleCampaigns = useHasMultipleSwitchableCampaigns();
  const showDock = isInGame && hasMultipleCampaigns;
  const { isOpen: isWidgetOpen, close: closeWidget } = useWidget();
  const scrollPositionRef = useRef<number>(0);
  const themeAnchorRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<
    HTMLElement | undefined
  >(undefined);
  useApplyTheme(themeAnchorRef);
  // Phones, in-game: the open layer leaves the game frame and covers the whole viewport, and the
  // shadow host itself moves onto <body> for the duration so no frame ancestor can clip or
  // out-stack it (see the hooks). Desktop stays frame-bound.
  const escapesFrame = useEscapesGameFrame(isInGame && isWidgetOpen);
  const { ready: hostReady, restore: restoreHost } = useHoistHostToBody(
    themeAnchorRef,
    escapesFrame
  );

  // In-game the open panel is a modal over a running game: focus in and back, Tab kept inside,
  // Escape closes (see the hook). Named after the campaign on screen — no new copy.
  const { t } = useTranslation();
  const layerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useInGameModal({
    active: isInGame && isWidgetOpen,
    layerRef,
    panelRef,
    onClose: closeWidget,
  });
  // The title STRING, not the campaign object: a progress push (missions, every second)
  // replaces the object, and selecting it re-rendered the whole panel on every push.
  const panelLabel = useAppStore((s) => {
    if (!isInGame) return undefined;
    const entries = [
      ...collectCampaignEntries(s, "active"),
      ...collectInWindowFinishedEntries(s),
    ];
    const key = s.selectedCampaignId;
    const campaign = (
      (key ? entries.find((o) => matchesKey(o.entry.campaign, key)) : null) ??
      entries[0]
    )?.entry.campaign;
    return campaign ? campaignDisplayTitle(campaign, t) : undefined;
  });

  useEffect(() => {
    if (!themeAnchorRef.current) return;
    const rootNode = themeAnchorRef.current.getRootNode();
    if (rootNode instanceof ShadowRoot) {
      setPortalContainer(themeAnchorRef.current);
    }
  }, []);

  const openEvent = getWidgetOpenEvent(variant);
  const closeEvent = getWidgetCloseEvent(variant);
  const stateChangeEvent = getWidgetStateChangeEvent(variant);

  useEffect(() => {
    if (!isWidgetOpen) return;
    if (isInGame) {
      // Frame-bound (desktop): the panel is a box inside the game, so locking the host page's
      // scroll would be intrusive. Viewport-bound (phones): the layer covers the page, so the game
      // page must not scroll under it — but only overflow, never `position: fixed` on the body:
      // a game whose canvas is sized off the body would collapse with it.
      if (!escapesFrame) return;
      const html = document.documentElement.style;
      const body = document.body.style;
      const previous = [html.overflow, body.overflow];
      html.overflow = "hidden";
      body.overflow = "hidden";
      return () => {
        [html.overflow, body.overflow] = previous;
      };
    }
    // On-site: a full-bleed sheet over the casino page; freeze the page where it was.
    {
      scrollPositionRef.current = window.scrollY;
      const bodyStyle = document.body.style;
      bodyStyle.position = "fixed";
      bodyStyle.top = `-${scrollPositionRef.current}px`;
      bodyStyle.width = "100%";
      bodyStyle.overflow = "hidden";

      return () => {
        const savedScrollY = scrollPositionRef.current;
        bodyStyle.position = "";
        bodyStyle.top = "";
        bodyStyle.width = "";
        bodyStyle.overflow = "";
        window.scrollTo(0, savedScrollY);
      };
    }
  }, [isWidgetOpen, isInGame, escapesFrame]);

  useEffect(() => {
    const handleOpen = () => {
      // Every open starts on Home (product rule): the memory router is a
      // module singleton, so without this it resumes on whatever campaign page
      // the widget was closed on. Reset synchronously in the open event —
      // BEFORE the open state flips and React paints — so the stale page never
      // flashes. In-game is exempt: its view is campaign-centric (no home
      // route) and auto-selects.
      if (!isInGame) {
        useAppStore.getState().setSelectedCampaignId(null);
        if (routerOnSite.state.location.pathname !== homeRoute) {
          void routerOnSite.navigate(homeRoute);
        }
      }
      window.dispatchEvent(new CustomEvent(stateChangeEvent, { detail: true }));
    };
    const handleClose = () => {
      window.dispatchEvent(
        new CustomEvent(stateChangeEvent, { detail: false })
      );
    };

    window.addEventListener(openEvent, handleOpen);
    window.addEventListener(closeEvent, handleClose);
    return () => {
      window.removeEventListener(openEvent, handleOpen);
      window.removeEventListener(closeEvent, handleClose);
    };
  }, [openEvent, closeEvent, stateChangeEvent, isInGame]);

  const router = variant === "on-site" ? routerOnSite : routerInGame;

  const navigateToCampaign = useCallback<NavigateToCampaign>(
    (key) => {
      useAppStore.getState().setSelectedCampaignId(key);
      // Campaign routes carry no id — the page follows the selection — so
      // only a player outside the campaign section is moved; one already on
      // a campaign tab stays put.
      if (!isCampaignRoute(router.state.location.pathname)) {
        void router.navigate(campaignRoute);
      }
    },
    [router]
  );

  return (
    <PortalContainerContext.Provider value={portalContainer}>
      {/* `reducedMotion="user"`: under prefers-reduced-motion every transform animation in the
          widget becomes instant while opacity fades stay — the static fallback, in one place. */}
      <MotionConfig reducedMotion="user">
        <CampaignNavigationContext.Provider value={navigateToCampaign}>
          <div ref={themeAnchorRef}>
            {/* Outside the open/closed gate on purpose: notifications arrive while
            the player is in a game with the panel closed. */}
            <Toaster />
            {/* A reveal started from the in-game button plays here while
                the panel is closed; open, the campaign's card owns it. The
                in-game widget only: it is the button's (an on-site widget on
                the same page would play a second window). */}
            {isInGame && !isWidgetOpen && <ClosedPanelReveal />}
            {/* The host goes back into the frame only once the layer has animated out. */}
            <AnimatePresence onExitComplete={restoreHost}>
              {isWidgetOpen && hostReady && (
                <motion.div
                  ref={layerRef}
                  className={cn(
                    "pointer-events-auto inset-0 z-9999 flex items-center justify-center md:p-2",
                    // In-game the panel floats over a running game: keep an inset on every side even on
                    // phones so the dimmed game stays visible around it and it reads as a layer, not
                    // a page. `igw-dock-room` (phones) leaves room at the bottom for the campaign
                    // sheet, minus the tuck of the panel behind it. On-site it is a full-bleed sheet.
                    // Phones: the same 20px of dimmed game on top and both sides — one even frame
                    // around the panel, wide enough that the game clearly shows behind it. The close
                    // button hangs on the panel's top-right corner inside that frame. Phones leave
                    // the frame for the viewport (`fixed`), so the top inset also clears the notch
                    // via the safe-area inset; desktop stays a box inside the frame.
                    isInGame &&
                      (escapesFrame
                        ? "fixed px-5 pt-[calc(1.25rem+env(safe-area-inset-top,0px))] pb-5 md:p-2 md:pt-[calc(2.5rem+env(safe-area-inset-top,0px))]"
                        : "absolute px-5 pt-5 pb-5 md:p-2 md:pt-10 lg:pt-2"),
                    // Sideways there is no height to spare: the panel runs from the top edge to the
                    // bottom (only the safe area above), and leaves a wider margin on the right so
                    // the game stays in view beside it (the dock's room on the left comes from
                    // .igw-dock-room).
                    isInGame &&
                      "ls:pt-[env(safe-area-inset-top,0px)] ls:pr-10 ls:pb-0 ls:pl-3",
                    !isInGame && "fixed p-0",
                    showDock && "igw-dock-room"
                  )}
                  style={{ zIndex: 9999 }}
                >
                  <motion.div
                    variants={fadeInAnimation}
                    transition={{ duration: 0.2, ...frameloopFade }}
                    className={cn(
                      "anim-layer absolute inset-0 touch-none",
                      isInGame && "igw-scrim"
                    )}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    onClick={closeWidget}
                    style={{
                      touchAction: "none",
                      // In-game: a theme-tinted, low-opacity wash (`.igw-scrim`) so the game shows
                      // through and the panel's edge reads against it. (No backdrop blur: it would
                      // sit over a live canvas.) On-site keeps the plain dim.
                      background: isInGame
                        ? undefined
                        : "linear-gradient(to top, rgba(0, 0, 0, 0.33),rgba(0, 0, 0, 0.44))",
                    }}
                    aria-hidden="true"
                  />
                  <motion.div
                    ref={panelRef}
                    {...(isInGame && {
                      role: "dialog",
                      "aria-modal": true,
                      "aria-label": panelLabel,
                      tabIndex: -1,
                    })}
                    className={cn(
                      "anim-layer relative z-50 mx-auto flex h-full w-full max-w-6xl flex-col outline-none lg:max-h-205",
                      // Lift + hairline edge so the panel separates from the game behind it.
                      // `igw-dock-host` publishes --igw-dock-inset (how far the dock overlaps the
                      // panel) to the scroll region inside.
                      // Sideways the panel touches the top and bottom edges and tucks behind the
                      // dock on the left: only its right corners stay rounded.
                      isInGame &&
                        "igw-panel-edge rounded-2xl ls:rounded-l-none",
                      showDock && "igw-dock-host"
                    )}
                    variants={widgetContainerAnimation}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <RouterProvider router={router} />
                  </motion.div>
                  {/* Phones: close sits on the panel's top-right corner, hanging the SAME 8px past
                    the panel's top edge and past its right edge (panel: top 40 / right 12 in the
                    frame → button top 32 / right 4), so it reads as pinned to the corner rather than
                    drifting. Never covers the art; same glass surface as the tab track so it reads
                    as widget chrome over anything. */}
                  {isInGame && (
                    // Same variants as the panel: it rises, settles and drops away in lockstep with
                    // the corner it sits on.
                    <motion.div
                      variants={widgetContainerAnimation}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className={cn(
                        // The panel's top-right is at 20/20: hang 8px past it on both axes.
                        "anim-layer absolute right-3 z-80 md:right-1 lg:hidden",
                        escapesFrame
                          ? "top-[calc(0.75rem+env(safe-area-inset-top,0px))] md:top-[calc(2rem+env(safe-area-inset-top,0px))]"
                          : "top-3 md:top-8",
                        // Sideways the close lives inside the header row (CampaignShell).
                        "ls:hidden"
                      )}
                    >
                      <TabRowCloseButton onClick={closeWidget} />
                    </motion.div>
                  )}
                  {/* Phones: the campaign selector is a bottom sheet the panel tucks behind — a sibling
                  of the panel, so nothing clips it and its shadow falls onto the panel. */}
                  {showDock && <InGameCampaignDock />}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </CampaignNavigationContext.Provider>
      </MotionConfig>
    </PortalContainerContext.Provider>
  );
};
